import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getGeoLocation, submitLead } from '@/services/api';

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  country_code: z.string(),
  contact_number: z.string().regex(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
  message: z.string().optional(),
});

const contactInfo = [
  {
    icon: MapPin,
    title: 'Office Address',
    details: ['1300 S Grand Ave Building A', 'Santa Ana, CA 92705'],
    color: 'blue'
  },
  {
    icon: Phone,
    title: 'Phone Number',
    details: ['+1 (877) 638-0328'],
    link: 'tel:+18776380328',
    color: 'purple'
  },
  {
    icon: Mail,
    title: 'Email Address',
    details: ['info@usbrandexperts.com'],
    link: 'mailto:info@usbrandexperts.com',
    color: 'cyan'
  },
  {
    icon: Clock,
    title: 'Business Hours',
    details: ['Monday - Friday: 9:00 AM - 6:00 PM', 'Saturday - Sunday: Closed'],
    color: 'pink'
  }
];

export const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { country_code: '+1' }
  });

  React.useEffect(() => {
    const fetchGeoData = async () => {
      const data = await getGeoLocation();
      setGeoData(data);
      
      if (data?.data?.countryCode) {
        const countryCodeMap = {
          'US': '+1', 'GB': '+44', 'IN': '+91', 'AU': '+61', 'CN': '+86',
          'JP': '+81', 'DE': '+49', 'FR': '+33', 'IT': '+39', 'ES': '+34',
          'RU': '+7', 'BR': '+55', 'MX': '+52', 'KR': '+82', 'NL': '+31',
          'SE': '+46', 'CH': '+41', 'NO': '+47', 'DK': '+45', 'PL': '+48',
          'SG': '+65', 'MY': '+60', 'TH': '+66', 'VN': '+84', 'ID': '+62',
          'PH': '+63', 'PK': '+92', 'BD': '+880', 'AE': '+971', 'SA': '+966',
          'ZA': '+27', 'NG': '+234', 'EG': '+20', 'KE': '+254', 'NZ': '+64',
          'IE': '+353', 'PT': '+351', 'GR': '+30', 'BE': '+32', 'AT': '+43', 'FI': '+358'
        };
        const dialCode = countryCodeMap[data.data.countryCode];
        if (dialCode) {
          setValue('country_code', dialCode);
        }
      }
    };
    
    fetchGeoData();
  }, [setValue]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await submitLead(data, geoData);
      
      // Handle 200 Success - Redirect to application page
      if (result.success && result.statusCode === 200 && result.customerId) {
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        navigate(`/application/main?customerid=${result.customerId}`);
        reset();
      } 
      // Handle 401 Unauthorized
      else if (result.statusCode === 401) {
        toast.error(result.message || 'Domain not configured. Please contact support.');
      }
      // Handle 400 Bad Request
      else if (result.statusCode === 400) {
        toast.error(result.message || 'Invalid data provided. Please check your information.');
      }
      // Handle other errors
      else {
        toast.error(result.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/2 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent">
                Get In Touch
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              Have questions about trademark registration or need expert guidance? We're here to help. Reach out to our team and let's protect your brand together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative">
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, idx) => {
              const colorClasses = {
                blue: 'from-blue-600 to-blue-400 shadow-blue-500/50',
                purple: 'from-purple-600 to-purple-400 shadow-purple-500/50',
                cyan: 'from-cyan-600 to-cyan-400 shadow-cyan-500/50',
                pink: 'from-pink-600 to-pink-400 shadow-pink-500/50'
              };

              return (
                <Card key={idx} className="bg-gradient-to-br from-slate-900 to-blue-950 border-2 border-blue-500/40 hover:border-blue-400 hover:shadow-[0_0_40px_rgba(59,130,246,0.6)] transition-all duration-300 group">
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto bg-gradient-to-br ${colorClasses[info.color]} rounded-full flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <info.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-heading font-bold text-white mb-3">
                      {info.title}
                    </h3>
                    <div className="space-y-1">
                      {info.details.map((detail, detailIdx) => (
                        info.link ? (
                          <a
                            key={detailIdx}
                            href={info.link}
                            className="block text-sm text-gray-300 hover:text-blue-400 transition-colors"
                          >
                            {detail}
                          </a>
                        ) : (
                          <p key={detailIdx} className="text-sm text-gray-300">
                            {detail}
                          </p>
                        )
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-blue-950 via-slate-900 to-purple-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Send Us a Message
              </span>
            </h2>
            <p className="text-lg text-gray-300">
              Fill out the form below and our team will respond within 24 hours
            </p>
          </div>

          <Card className="bg-gradient-to-br from-slate-900 to-blue-950 border-2 border-blue-500/40 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                  <Input
                    id="name"
                    {...register('name')}
                    placeholder="Your full name"
                    className="mt-2 bg-slate-800 border-gray-700 text-white"
                  />
                  {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    {...register('email')}
                    placeholder="your@email.com"
                    className="mt-2 bg-slate-800 border-gray-700 text-white"
                  />
                  {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <Label className="text-gray-300">Phone Number *</Label>
                  <div className="flex gap-2 mt-2">
                    <Select defaultValue="+1" onValueChange={(value) => setValue('country_code', value)}>
                      <SelectTrigger className="w-32 bg-slate-800 border-gray-700 text-white">
                        <SelectValue className="text-white" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="+1">🇺🇸 +1</SelectItem>
                        <SelectItem value="+44">🇬🇧 +44</SelectItem>
                        <SelectItem value="+91">🇮🇳 +91</SelectItem>
                        <SelectItem value="+61">🇦🇺 +61</SelectItem>
                        <SelectItem value="+86">🇨🇳 +86</SelectItem>
                        <SelectItem value="+81">🇯🇵 +81</SelectItem>
                        <SelectItem value="+49">🇩🇪 +49</SelectItem>
                        <SelectItem value="+33">🇫🇷 +33</SelectItem>
                        <SelectItem value="+39">🇮🇹 +39</SelectItem>
                        <SelectItem value="+34">🇪🇸 +34</SelectItem>
                        <SelectItem value="+7">🇷🇺 +7</SelectItem>
                        <SelectItem value="+55">🇧🇷 +55</SelectItem>
                        <SelectItem value="+52">🇲🇽 +52</SelectItem>
                        <SelectItem value="+82">🇰🇷 +82</SelectItem>
                        <SelectItem value="+31">🇳🇱 +31</SelectItem>
                        <SelectItem value="+46">🇸🇪 +46</SelectItem>
                        <SelectItem value="+41">🇨🇭 +41</SelectItem>
                        <SelectItem value="+47">🇳🇴 +47</SelectItem>
                        <SelectItem value="+45">🇩🇰 +45</SelectItem>
                        <SelectItem value="+48">🇵🇱 +48</SelectItem>
                        <SelectItem value="+65">🇸🇬 +65</SelectItem>
                        <SelectItem value="+60">🇲🇾 +60</SelectItem>
                        <SelectItem value="+66">🇹🇭 +66</SelectItem>
                        <SelectItem value="+84">🇻🇳 +84</SelectItem>
                        <SelectItem value="+62">🇮🇩 +62</SelectItem>
                        <SelectItem value="+63">🇵🇭 +63</SelectItem>
                        <SelectItem value="+92">🇵🇰 +92</SelectItem>
                        <SelectItem value="+880">🇧🇩 +880</SelectItem>
                        <SelectItem value="+971">🇦🇪 +971</SelectItem>
                        <SelectItem value="+966">🇸🇦 +966</SelectItem>
                        <SelectItem value="+27">🇿🇦 +27</SelectItem>
                        <SelectItem value="+234">🇳🇬 +234</SelectItem>
                        <SelectItem value="+20">🇪🇬 +20</SelectItem>
                        <SelectItem value="+254">🇰🇪 +254</SelectItem>
                        <SelectItem value="+64">🇳🇿 +64</SelectItem>
                        <SelectItem value="+353">🇮🇪 +353</SelectItem>
                        <SelectItem value="+351">🇵🇹 +351</SelectItem>
                        <SelectItem value="+30">🇬🇷 +30</SelectItem>
                        <SelectItem value="+32">🇧🇪 +32</SelectItem>
                        <SelectItem value="+43">🇦🇹 +43</SelectItem>
                        <SelectItem value="+358">🇫🇮 +358</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      {...register('contact_number')}
                      type="tel"
                      maxLength={10}
                      placeholder="1234567890"
                      className="flex-1 bg-slate-800 border-gray-700 text-white"
                      onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                    />
                  </div>
                  {errors.contact_number && <p className="text-xs text-red-400 mt-1">{errors.contact_number.message}</p>}
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-300">Your Message (Optional)</Label>
                  <Textarea
                    id="message"
                    {...register('message')}
                    placeholder="Tell us how we can help you..."
                    rows={6}
                    className="mt-2 bg-slate-800 border-gray-700 text-white"
                  />
                  {errors.message && <p className="text-xs text-red-400 mt-1">{errors.message.message}</p>}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  size="lg"
                  className="w-full bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 hover:from-blue-700 hover:via-blue-600 hover:to-cyan-600 text-white font-bold text-lg py-6 transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Map Section (Optional) */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="rounded-2xl overflow-hidden border-2 border-blue-500/30 shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3317.7396985947393!2d-117.87015492346494!3d33.74467423405892!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dcddb9ee2e8c4f%3A0x3e5f7d5e5e5e5e5e!2s1300%20S%20Grand%20Ave%20Building%20A%2C%20Santa%20Ana%2C%20CA%2092705!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Office Location"
              className="w-full"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};
